import React, { useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Reservation } from "../types/Reservation";
import {
  getReservationBlockVisualStyle,
  RESERVATION_CONTAINER_STYLES,
  RESERVATION_TEXT_STYLES,
} from "../styles/reservationStyles";
import { getNombreSector } from "../../calendar/utils/zonas";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";
import { useReservationTooltip } from "../contexts/TooltipContext";
import { formatCurrency } from "../../shared/utils/formatters";

interface ReservationBlockProps {
  reserva: Reservation;
  startsOutsideMonth: boolean;
  endsOutsideMonth: boolean;
  onClick?: (reservation: Reservation) => void;
  isOverlapping?: boolean;
}

const ReservationBlock: React.FC<ReservationBlockProps> = ({
  reserva,
  startsOutsideMonth,
  endsOutsideMonth,
  onClick,
  isOverlapping = false,
}: ReservationBlockProps) => {
  const theme = useTheme();
  const { showTooltip, hideTooltip } = useReservationTooltip();

  // Determinar la zona a partir del número de departamento
  const sectorNombre = getNombreSector(reserva.departamento);
  const zonaKey = sectorNombre.toLowerCase();
  const isInternal = isInternalDepartment(reserva.departamento);
  const zonaColors =
    zonaKey === "interno"
      ? theme.palette.zonas.interno
      : theme.palette.zonas[zonaKey];

  const baseStyle = useMemo(() => {
    return getReservationBlockVisualStyle(
      startsOutsideMonth,
      endsOutsideMonth,
      zonaColors.border
    );
  }, [startsOutsideMonth, endsOutsideMonth, zonaColors.border]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick?.(reserva);
    },
    [onClick, reserva]
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Solo prevenir el comportamiento por defecto para eventos de touch/pointer
    // y solo si no es un evento de mouse
    if (e.pointerType === "touch" || e.pointerType === "pen") {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      const rect = event.currentTarget.getBoundingClientRect();
      showTooltip(reserva, {
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    },
    [reserva, showTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  const textColor = theme.palette.getContrastText(zonaColors.border);

  return (
    <Box
      className="reservation-block"
      sx={{
        ...baseStyle,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        position: "relative",
        transition: "box-shadow 180ms ease, opacity 180ms ease",
        ...(startsOutsideMonth
          ? {}
          : {
              borderLeft: `3px solid ${zonaColors.border}`,
            }),
        ...(isOverlapping && {
          border: "2px dashed #ff4444 !important",
          backgroundColor: "rgba(255, 68, 68, 0.05)",
          boxShadow: "0 0 8px rgba(255, 68, 68, 0.3)",
        }),
      }}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
        {startsOutsideMonth && (
          <Box
            sx={{
              position: "absolute",
              left: 2,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              color: textColor,
              opacity: 0.7,
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        <Box sx={{ ...RESERVATION_CONTAINER_STYLES, color: textColor }}>
          <Typography sx={RESERVATION_TEXT_STYLES.NOMBRE}>
            {reserva.nombre}
          </Typography>
          <Typography sx={RESERVATION_TEXT_STYLES.PRECIO}>
            {isInternal ? "Boedo" : formatCurrency(reserva.valor_total)}
          </Typography>
        </Box>
        {endsOutsideMonth && (
          <Box
            sx={{
              position: "absolute",
              right: 2,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              color: textColor,
              opacity: 0.7,
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
    </Box>
  );
};

export default React.memo(ReservationBlock);
